import { fassungsschild } from '@nac2s/shared'
import { useState } from 'react'
import { NavLink, Outlet } from 'react-router'
import { Wortmarke } from '@/teile/Wortmarke'
import { cx } from '@/utils/cx'
import { abmelden } from '../api/partner.js'
import { Anmelden } from '../pages/Anmelden.js'
import { Einrichtung } from '../pages/Einrichtung.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Rahmen der Partner-Oberfläche.
 *
 * **Die Anwendung ist eine weiße Karte auf hellgrauem Grund**, nicht eine
 * Seite mit Kopfleiste. Der Unterschied ist nicht Geschmack: Die Karte hat
 * einen Rand, an dem sie aufhört, und dadurch eine Ecke, aus der der Schimmer
 * kommen kann. Ohne sie schwimmt der Inhalt.
 *
 * Ohne Sitzung erscheint das Anmeldeformular an Ort und Stelle statt einer
 * Weiterleitung: Wer einen Link auf einen Kunden bekommt und nicht angemeldet
 * ist, landet nach der Anmeldung dort, wo er hinwollte.
 */

/**
 * `braucht` blendet einen Eintrag aus, wenn die Rolle das Recht nicht hat. Das
 * ist Bequemlichkeit und kein Schutz — durchgesetzt wird jedes Recht am
 * Endpunkt (403). Eine Navigation, die auf eine verschlossene Tür zeigt,
 * erzieht zum Wegklicken von Fehlermeldungen.
 */
const NAVIGATION = [
  { pfad: '/', beschriftung: 'Kunden', genau: true, braucht: null },
  { pfad: '/katalog', beschriftung: 'Katalog', genau: false, braucht: 'kunden.kaufmaennisch' },
  { pfad: '/konten', beschriftung: 'Konten', genau: false, braucht: 'konten.verwalten' },
  { pfad: '/unternehmen', beschriftung: 'Unternehmen', genau: false, braucht: 'partner.profil' },
  { pfad: '/berichte', beschriftung: 'Berichte', genau: false, braucht: null },
] as const

export function AppShell() {
  const { zustand, neuLaden } = useSitzung()
  const [meldetAb, setMeldetAb] = useState(false)

  if (zustand.status === 'unbekannt') {
    return (
      <p role="status" className="p-8 text-sm text-tertiary">
        Wird geladen …
      </p>
    )
  }

  if (zustand.status === 'fehler') {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="rounded-xl bg-error-primary p-4 text-sm text-error-primary">
          <p className="font-medium">Der Dienst ist nicht erreichbar</p>
          <p className="mt-1">{zustand.meldung}</p>
        </div>
      </div>
    )
  }

  // Ohne Sitzung: Ist der Stack ueberhaupt eingerichtet? Ein frischer Stack
  // zeigt den Assistenten statt der Anmeldung (ADR-0020).
  if (zustand.status === 'abgemeldet') return <Einrichtung sonst={<Anmelden />} />

  async function abmeldenUndNeuLaden() {
    setMeldetAb(true)
    try {
      await abmelden()
    } finally {
      // Auch wenn die Abmeldung scheitert, wird der Zustand neu geholt: Eine
      // Oberfläche, die Abmeldung nur vortäuscht, ist schlimmer als keine.
      setMeldetAb(false)
      await neuLaden()
    }
  }

  return (
    <div className="min-h-screen bg-secondary px-4 py-10">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-primary shadow-lg">
        <header className="flex flex-wrap items-center gap-3.5 border-b border-secondary px-5 py-[13px]">
          {/* „Kundenverwaltung", nicht „Partnerverwaltung": Hier meldet sich
              ein Partner an und verwaltet **seine Kunden**. Wie das Paket im
              Repository heisst (`partner-web`), geht den Benutzer nichts an. */}
          <Wortmarke beisatz="Kundenverwaltung" />

          <nav aria-label="Hauptbereiche" className="ml-1 flex gap-0.5">
            {NAVIGATION.filter(
              (eintrag) =>
                eintrag.braucht === null ||
                (zustand.sitzung.rechte as readonly string[]).includes(eintrag.braucht),
            ).map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.genau}
                className={({ isActive }) =>
                  cx(
                    'rounded-full px-3.5 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-akzent-flaeche font-semibold text-akzent-tief'
                      : 'text-tertiary hover:bg-secondary hover:text-primary',
                  )
                }
              >
                {eintrag.beschriftung}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="hidden items-center gap-2 text-xs text-tertiary sm:flex">
              <span className="size-[7px] animate-pulse rounded-full bg-fg-success-primary" />
              alles erreichbar
            </span>
            <span
              aria-hidden="true"
              className="grid size-8 place-items-center rounded-full bg-linear-to-br from-marke-blau to-marke-violett text-xs font-semibold text-white"
            >
              {initialen(zustand.sitzung.anzeigename)}
            </span>
            {/* Kein Bauteil mit Ring und Schatten: Die Leiste hat in der
                Vorlage 58 Pixel Hoehe, und eine erhabene Schaltflaeche macht
                sie hoeher — dann verschiebt sich alles darunter mit. */}
            <button
              type="button"
              onClick={abmeldenUndNeuLaden}
              disabled={meldetAb}
              className="cursor-pointer rounded-lg px-2.5 py-1.5 text-[13px] font-semibold text-primary transition-colors hover:bg-secondary disabled:opacity-50"
            >
              Abmelden
            </button>
          </div>
        </header>

        <Outlet />

        <Fassungszeile />
      </div>
    </div>
  )
}

/**
 * Die ausgelieferte Fassung, klein und immer sichtbar.
 *
 * `DOCUMENTATION.md` §1: Neben der Nummer steht der Git-Kurz-Hash — er
 * unterscheidet zwei Stände derselben Nummer. Der Hash kommt beim Bauen aus
 * `VITE_NADIA_BUILD`; fehlt er, bleibt es bei der Nummer.
 */
function Fassungszeile() {
  return (
    <footer className="border-t border-secondary px-6 py-3 text-right">
      <span className="text-[11.5px] text-quaternary tabular-nums" title="Ausgelieferte Fassung">
        VeeKy {fassungsschild(import.meta.env.VITE_NADIA_BUILD)}
      </span>
    </footer>
  )
}

/** Höchstens zwei Buchstaben — drei werden im Kreis zu klein. */
export function initialen(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((teil) => teil[0])
    .join('')
    .toUpperCase()
}
